// src/features/account/components/CreatorEarningsTab.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { DollarSign, TrendingUp, Clock, AlertCircle, RefreshCw, ChevronDown, Upload } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { logger } from '../../../utils/logger';
import StripeConnectButton from './StripeConnectButton';

const CreatorEarningsTab = () => {
  const [searchParams] = useSearchParams();
  const [connectStatus, setConnectStatus] = useState(null);
  const [earnings, setEarnings] = useState([]);
  const [totals, setTotals] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  const getAuthToken = () => {
    const userData = JSON.parse(localStorage.getItem('workos_user') || '{}');
    return userData.accessToken;
  };

  const fetchConnectStatus = useCallback(async () => {
    try {
      const token = getAuthToken();
      if (!token) return;

      const response = await fetch('/.netlify/functions/stripe-connect-status', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setConnectStatus(data);
      }
    } catch (err) {
      logger.error('Failed to fetch connect status:', err);
    }
  }, []);

  const fetchEarnings = useCallback(async (offset = 0, append = false) => {
    try {
      const token = getAuthToken();
      if (!token) return;

      const response = await fetch(`/.netlify/functions/creator-earnings?limit=50&offset=${offset}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        if (append) {
          setEarnings(prev => [...prev, ...data.earnings]);
        } else {
          setEarnings(data.earnings);
        }
        setTotals(data.totals);
        setPagination(data.pagination);
      }
    } catch (err) {
      logger.error('Failed to fetch earnings:', err);
      setError('Failed to load earnings');
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchConnectStatus(), fetchEarnings()]);
      setLoading(false);
    };
    loadData();
  }, [fetchConnectStatus, fetchEarnings]);

  // Auto-refresh when returning from Stripe Connect onboarding
  useEffect(() => {
    const connectParam = searchParams.get('connect');
    if (connectParam === 'complete' || connectParam === 'refresh') {
      handleRefresh();
    }
  }, [searchParams]);

  const handleLoadMore = async () => {
    if (!pagination?.has_more) return;
    setLoadingMore(true);
    await fetchEarnings(pagination.offset + pagination.limit, true);
    setLoadingMore(false);
  };

  const handleRefresh = async () => {
    setLoading(true);
    setError(null);
    await Promise.all([fetchConnectStatus(), fetchEarnings()]);
    setLoading(false);
  };

  const formatCents = (cents) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  const getStatusBadge = (status) => {
    const styles = {
      transferred: 'bg-green-500/20 text-green-400',
      paid: 'bg-green-500/20 text-green-400',
      pending_transfer: 'bg-yellow-500/20 text-yellow-400',
      pending_onboard: 'bg-blue-500/20 text-blue-400',
      failed: 'bg-red-500/20 text-red-400'
    };

    const labels = {
      transferred: 'Transferred',
      paid: 'Paid',
      pending_transfer: 'Pending',
      pending_onboard: 'Awaiting Connect',
      failed: 'Failed'
    };

    return (
      <span className={`text-xs px-2 py-0.5 rounded ${styles[status] || 'bg-gray-500/20 text-gray-400'}`}>
        {labels[status] || status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="space-y-4 animate-pulse">
        <div className="h-6 w-40 bg-gray-800 rounded" />
        <div className="grid grid-cols-3 gap-4">
          <div className="h-24 bg-gray-800 rounded-lg" />
          <div className="h-24 bg-gray-800 rounded-lg" />
          <div className="h-24 bg-gray-800 rounded-lg" />
        </div>
        <div className="h-48 bg-gray-800 rounded-lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-white">Creator Earnings</h2>
        <button
          onClick={handleRefresh}
          className="p-2 text-gray-400 hover:text-white transition-colors"
          title="Refresh"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Stripe Connect Status */}
      <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
        <h3 className="text-sm font-medium text-gray-300 mb-3">Payout Account</h3>
        <StripeConnectButton
          connectStatus={connectStatus}
          onStatusChange={handleRefresh}
        />
      </div>

      {/* Totals Cards */}
      {totals && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <div className="flex items-center space-x-2 text-gray-400 mb-1">
              <DollarSign className="w-4 h-4" />
              <span className="text-sm">Total Earned</span>
            </div>
            <p className="text-2xl font-bold text-white">
              {formatCents(totals.total_earned_cents)}
            </p>
          </div>

          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <div className="flex items-center space-x-2 text-gray-400 mb-1">
              <TrendingUp className="w-4 h-4" />
              <span className="text-sm">Transferred</span>
            </div>
            <p className="text-2xl font-bold text-green-400">
              {formatCents(totals.total_transferred_cents)}
            </p>
          </div>

          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <div className="flex items-center space-x-2 text-gray-400 mb-1">
              <Clock className="w-4 h-4" />
              <span className="text-sm">Pending</span>
            </div>
            <p className="text-2xl font-bold text-yellow-400">
              {formatCents(totals.total_pending_cents + totals.total_pending_onboard_cents)}
            </p>
          </div>
        </div>
      )}

      {/* Pending Onboard Notice */}
      {totals && totals.total_pending_onboard_cents > 0 && !connectStatus?.connected && (
        <div className="bg-blue-900/20 border border-blue-700 rounded-lg p-4 flex items-start space-x-3">
          <AlertCircle className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-blue-300 text-sm font-medium">
              You have {formatCents(totals.total_pending_onboard_cents)} in unclaimed earnings!
            </p>
            <p className="text-blue-400/70 text-sm mt-1">
              Connect your Stripe account above to receive your payouts.
            </p>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-900/20 border border-red-700 rounded-lg p-4">
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      )}

      {/* Earnings Table */}
      <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-700">
          <h3 className="text-sm font-medium text-gray-300">Transaction History</h3>
        </div>

        {earnings.length === 0 ? (
          <div className="px-4 py-12 text-center text-gray-500">
            <DollarSign className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-white font-medium">No earnings yet</p>
            <p className="text-sm mt-1">Earnings will appear here when users purchase your workflows.</p>
            <a
              href="/exchange"
              className="inline-flex items-center space-x-2 mt-4 px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white text-sm rounded-lg transition-colors"
            >
              <Upload className="w-4 h-4" />
              <span>Upload a Workflow</span>
            </a>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-gray-400 border-b border-gray-700">
                    <th className="text-left px-4 py-2 font-medium">Workflow</th>
                    <th className="text-right px-4 py-2 font-medium">Sale</th>
                    <th className="text-right px-4 py-2 font-medium">Your Share</th>
                    <th className="text-center px-4 py-2 font-medium">Status</th>
                    <th className="text-right px-4 py-2 font-medium">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {earnings.map((earning) => (
                    <tr key={earning.id} className="border-b border-gray-700/50 hover:bg-gray-700/30">
                      <td className="px-4 py-3 text-white truncate max-w-[200px]">
                        {earning.listing_title}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-300">
                        {formatCents(earning.amount_cents)}
                      </td>
                      <td className="px-4 py-3 text-right text-green-400 font-medium">
                        {formatCents(earning.creator_share_cents)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {getStatusBadge(earning.status)}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-400">
                        {new Date(earning.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {pagination?.has_more && (
              <div className="px-4 py-3 border-t border-gray-700 text-center">
                <button
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  className="flex items-center space-x-1 mx-auto text-sm text-gray-400 hover:text-white transition-colors disabled:opacity-50"
                >
                  {loadingMore ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <ChevronDown className="w-4 h-4" />
                  )}
                  <span>{loadingMore ? 'Loading...' : 'Load More'}</span>
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Revenue Split Info */}
      <div className="text-center text-gray-500 text-xs">
        Creators earn 70% of each sale. Platform retains 30%.
      </div>
    </div>
  );
};

export default CreatorEarningsTab;
